import { Button, Stack } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { openDialog } from '../../store/states/DialogSlice';
import { AppState } from 'store/AppStore';
import { revertMarketAssociations } from '../../store/states/ProcosSlice';
import { Master, ECommonDialogType } from '../../data/Constants';

import type { IButtonSectionProps } from '../../../types/IComponentTypes';

/**
 * This component renders the Save, cancel and manage options buttons
 * @returns {JSX.Element} a ButtonSection component.
 */
const ButtonSection = ({ handleClickOk }: IButtonSectionProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const procosState = useSelector((state: AppState) => state.procos);
  const isDataUpdated = procosState.isDataUpdated;
  const isEditMode = procosState.isEditMode;
  const workItem = procosState.activeWorkItem;

  const hasAllMarketsRemoved = () => {
    return (
      procosState.product.ma.updatedMarkets.length === 0 &&
      procosState.product.ma.initialMarkets.length > 0
    );
  };

  // Get the save message as a ReactElement
  const getSaveMessage = (): React.ReactElement => {
    if (hasAllMarketsRemoved()) {
      return <Trans i18nKey="messages.submit.allMarketsRemoved" />;
    }

    if (workItem && workItem.name !== Master.name) {
      return (
        <Trans
          i18nKey="messages.submit.exist"
          values={{ workItemName: workItem.name }}
          components={{ 1: <b /> }}
        />
      );
    }

    return <Trans i18nKey="messages.submit.new" components={{ 1: <b /> }} />;
  };

  // Handle save button click
  const handleClickSave = () => {
    const isAllMarketsRemoved = hasAllMarketsRemoved();
    dispatch(
      openDialog({
        type: isAllMarketsRemoved ? ECommonDialogType.Error : ECommonDialogType.Confirm,
        title: isAllMarketsRemoved ? t('dialogBox.error') : t('dialogBox.save'),
        data: getSaveMessage(),
        handleClickOk: isAllMarketsRemoved ? handleConfirmRevert : handleClickOk,
      })
    );
  };

  // Handle revert confirmation
  const handleConfirmRevert = () => {
    dispatch(revertMarketAssociations());
  };

  // Handle revert button click
  const handleClickRevert = () => {
    if (!workItem) {
      return;
    }
    dispatch(
      openDialog({
        type: ECommonDialogType.Confirm,
        title: t('dialogBox.revert'),
        data: (
          <Trans
            i18nKey="messages.submit.revert"
            values={{ workItemName: workItem.name }}
            components={{ 1: <b /> }}
          />
        ),
        handleClickOk: handleConfirmRevert,
      })
    );
  };

  return (
    <div id="procos-button-section" className="align-center d-flex justify-end">
      {isEditMode && (
        <Stack direction="row" spacing={2}>
          <Button
            id="save-button"
            data-testid="save-button"
            variant="contained"
            className="text-capitalize common-btn"
            disabled={!isDataUpdated}
            onClick={handleClickSave}
          >
            {t('button.save')}
          </Button>
          <Button
            id="revert-button"
            data-testid="revert-button"
            variant="contained"
            className="text-capitalize common-btn"
            disabled={!isDataUpdated}
            onClick={handleClickRevert}
          >
            {t('button.revert')}
          </Button>
        </Stack>
      )}
    </div>
  );
};

export default ButtonSection;
